/**
 * Build the export filename for a certificate (PDF, HTML, Excel).
 *
 * Format: "{LOT_PREFIX} ZMSM {CLIENT_REF_RANGE}" (without extension)
 *   - LOT_PREFIX: first sample's sample_id lot prefix with hyphen stripped
 *                 (e.g. "LZ-04049-26-01" -> "LZ04049")
 *   - ZMSM:       hardcoded constant (Zgounder Mining)
 *   - CLIENT_REF_RANGE: first non-BIS sample's external_id, concatenated
 *                       with the last 2 characters of the last non-BIS
 *                       sample's external_id.
 *                       e.g. first="TF260034340", last="TF260034364"
 *                            -> "TF26003434064"
 *                       For INDIVIDUAL certificates (single sample), only
 *                       the single external_id is used (no concat).
 *                       If external_ids are missing, this segment is dropped.
 *
 * Accepts either:
 *   - a Certificate object (has .sample / .samples and .certificate_number)
 *   - certificate_data (has .sample / .samples and .batch / .certificate)
 *
 * Never returns empty — always falls back to something sensible.
 */
function cleanExternalId(raw: any): string {
  if (raw === undefined || raw === null) return '';
  let s = String(raw).trim();
  // Strip "BIS(xxxxx)" wrappers
  const bisWrap = s.match(/^BIS\s*\(\s*(.+?)\s*\)\s*$/i);
  if (bisWrap) s = bisWrap[1].trim();
  // Strip "(Bis xxxxx)" wrappers (seen in prod data)
  const bisWrap2 = s.match(/^\(\s*Bis\s+(.+?)\s*\)\s*$/i);
  if (bisWrap2) s = bisWrap2[1].trim();
  // Strip leading "Bis " prefix if still present
  s = s.replace(/^Bis\s+/i, '').trim();
  return s;
}

function isBisExternalId(raw: any): boolean {
  if (raw === undefined || raw === null) return false;
  return /bis/i.test(String(raw));
}

function getExternalIdOf(s: any): string {
  return s?.external_id || s?.sample_data?.external_id || '';
}

export function buildCertificateFilename(source: any): string {
  if (!source) return 'Certificat';

  // --- Lot prefix (unchanged) ---
  const firstAny: any =
    (Array.isArray(source.samples) && source.samples[0]) ||
    source.sample ||
    {};

  const sampleId: string =
    firstAny.sample_id ||
    firstAny.sample_data?.sample_id ||
    '';

  let lotPart = '';
  const match = String(sampleId).match(/^([A-Za-z]+)-?(\d+)/);
  if (match) {
    lotPart = (match[1] + match[2]).toUpperCase();
  } else if (source.certificate_number) {
    lotPart = String(source.certificate_number).replace(/[^A-Za-z0-9]/g, '_');
  } else if (source.certificate?.number) {
    lotPart = String(source.certificate.number).replace(/[^A-Za-z0-9]/g, '_');
  } else {
    lotPart = 'LOT';
  }

  // --- Client-reference range: first_ext_id + last 2 chars of last_ext_id ---
  let clientRefPart = '';
  const samplesArr: any[] = Array.isArray(source.samples) ? source.samples : [];
  if (samplesArr.length > 0) {
    const nonBis = samplesArr.filter((s) => !isBisExternalId(getExternalIdOf(s)));
    const firstSample = nonBis[0] || samplesArr[0];
    const lastSample = nonBis[nonBis.length - 1] || samplesArr[samplesArr.length - 1];
    const firstExt = cleanExternalId(getExternalIdOf(firstSample));
    const lastExt = cleanExternalId(getExternalIdOf(lastSample));
    if (firstExt) {
      if (lastSample === firstSample || !lastExt) {
        // Single (non-BIS) sample → no concat
        clientRefPart = firstExt;
      } else {
        clientRefPart = firstExt + lastExt.slice(-2);
      }
    }
  } else if (source.sample) {
    // INDIVIDUAL certificate
    const ext = cleanExternalId(getExternalIdOf(source.sample));
    if (ext) clientRefPart = ext;
  }

  const parts = [lotPart, 'ZMSM'];
  if (clientRefPart) parts.push(clientRefPart);

  // Sanitize for filesystem: remove characters illegal on Windows/macOS/Linux
  // and collapse whitespace.
  const raw = parts.join(' ');
  const safe = raw.replace(/[\\/:*?"<>|\r\n\t]/g, '_').replace(/\s+/g, ' ').trim();
  return safe || 'Certificat';
}

/**
 * Decide whether the LAB_WEIGHT column should appear in a batch certificate.
 *
 * Rule (2026-04-22 — user request):
 *   2.5 kg is the reception-form placeholder (= "no real measurement"),
 *   so samples whose poids_kg is missing, null, or exactly 2.5 don't count.
 *   The column is shown only if at least one sample has a genuine, non-2.5 weight.
 *
 * Accepts either:
 *   - a certificate_data object (has .samples or .sample)
 *   - a raw samples array
 */
export const DEFAULT_PLACEHOLDER_WEIGHT_KG = 2.5;

/**
 * Zgounder's preferred element order for certificate displays.
 * Matches the order stored by the reception form in
 * `sample.metadonnees.selected_elements` and the order used in
 * the SAA supervisor view.
 */
export const ZGOUNDER_PREFERRED_ELEMENT_ORDER = ['Ag', 'Cu', 'Fe', 'Pb', 'Zn'];

/**
 * Returns a deterministically ordered list of elements for a certificate:
 *   1. First priority: the first sample's `selected_elements` array order
 *      (as chosen at reception time).
 *   2. Fallback: Zgounder preferred order (Ag, Cu, Pb, Fe, Zn).
 *   3. Any additional elements not covered above are appended at the end.
 *
 * @param source  certificate_data-like object (has `samples` or `sample`)
 * @param availableElements  optional set/array to restrict which elements are
 *                           included (e.g. elements actually present in the data).
 *                           If omitted, all elements from the preference list
 *                           are returned.
 */
export function orderCertificateElements(
  source: any,
  availableElements?: string[] | Set<string> | null,
): string[] {
  const samples: any[] = Array.isArray(source?.samples)
    ? source.samples
    : source?.sample
      ? [source.sample]
      : [];

  const firstMeta =
    samples[0]?.sample_data?.metadonnees || samples[0]?.metadonnees || {};

  const selectedElements: string[] =
    Array.isArray(firstMeta.selected_elements) && firstMeta.selected_elements.length > 0
      ? firstMeta.selected_elements.filter((el: any) => typeof el === 'string')
      : [];

  const availableSet: Set<string> | null = availableElements
    ? availableElements instanceof Set
      ? (availableElements as Set<string>)
      : new Set<string>(availableElements as string[])
    : null;
  const isInAvailable = (el: string) => !availableSet || availableSet.has(el);

  const result: string[] = [];
  const push = (el: string) => {
    if (el && !result.includes(el) && isInAvailable(el)) result.push(el);
  };

  selectedElements.forEach(push);
  ZGOUNDER_PREFERRED_ELEMENT_ORDER.forEach(push);
  if (availableSet) availableSet.forEach(push);
  return result;
}

export function certificateHasMeaningfulWeight(source: any): boolean {
  if (!source) return false;
  const samples: any[] = Array.isArray(source)
    ? source
    : Array.isArray(source.samples)
      ? source.samples
      : source.sample
        ? [source.sample]
        : [];

  if (samples.length === 0) return false;

  return samples.some((s: any) => {
    const meta = s?.sample_data?.metadonnees || s?.metadonnees || {};
    const raw = meta.poids_kg;
    if (raw === undefined || raw === null || raw === '') return false;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
    if (!Number.isFinite(num)) return false;
    return num !== DEFAULT_PLACEHOLDER_WEIGHT_KG;
  });
}
